import React, { useState, useEffect } from 'react';
import { FaTimes, FaCamera } from 'react-icons/fa';
import { contactService, getImageUrl } from '../services/contactService';

const COUNTRY_CODES = [
  { value: '+1', label: '+1 (US/CA)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+49', label: '+49 (DE)' },
  { value: '+33', label: '+33 (FR)' },
  { value: '+39', label: '+39 (IT)' },
  { value: '+34', label: '+34 (ES)' },
  { value: '+61', label: '+61 (AU)' },
  { value: '+81', label: '+81 (JP)' },
  { value: '+86', label: '+86 (CN)' },
];

const GROUP_CATEGORIES = [
  { value: 'none', label: 'None' },
  { value: 'family', label: 'Family' },
  { value: 'work', label: 'Work' },
  { value: 'friends', label: 'Friends' },
  { value: 'other', label: 'Other' },
  { value: 'custom', label: 'Custom' },
];

const ContactModal = ({ contact, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    country_code: '+1',
    group_category: 'none',
    custom_group: '',
    birthday: '',
    comment: '',
    is_favorite: false,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone_number: contact.phone_number || '',
        country_code: contact.country_code || '+1',
        group_category: contact.group_category || 'none',
        custom_group: contact.custom_group || '',
        birthday: contact.birthday || '',
        comment: contact.comment || '',
        is_favorite: contact.is_favorite || false,
      });
      setPhotoPreview(contact.photo_path ? getImageUrl(contact.photo_path) : null);
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data without empty strings
      const submitData = {
        ...formData,
        email: formData.email || undefined,
        custom_group: formData.custom_group || undefined,
        birthday: formData.birthday || undefined,
        comment: formData.comment || undefined,
      };

      // Save contact data
      const savedContact = await onSave(submitData);

      // Upload photo if selected
      if (photoFile && (contact?.id || savedContact?.id)) {
        await contactService.uploadPhoto(contact?.id || savedContact.id, photoFile);
      }
    } catch (error) {
      // Error is handled in parent component
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" id="contact-modal-overlay" onClick={onClose}>
      <div className="modal-content" id="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="modal-title">{contact ? 'Edit Contact' : 'New Contact'}</h2>
          <button id="modal-close-btn" className="btn-icon" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} id="contact-form">
          <div className="photo-upload">
            <img
              src={photoPreview || getImageUrl(null)}
              alt="Contact"
              className="photo-preview"
              id="photo-preview"
            />
            <label htmlFor="photo-input" className="btn btn-secondary">
              <FaCamera /> {photoPreview ? 'Change Photo' : 'Add Photo'}
            </label>
            <input
              id="photo-input"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="name-input">
              Name <span style={{ color: 'var(--danger-color)' }}>*</span>
            </label>
            <input
              id="name-input"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter contact name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email-input-modal">Email</label>
            <input
              id="email-input-modal"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country-code-select">
                Country Code <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <select
                id="country-code-select"
                name="country_code"
                value={formData.country_code}
                onChange={handleChange}
                required
              >
                {COUNTRY_CODES.map((code) => (
                  <option key={code.value} value={code.value}>
                    {code.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="phone-input">
                Phone Number <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <input
                id="phone-input"
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                required
                placeholder="10-digit number"
                pattern="[0-9]{10}"
                maxLength="10"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="group-select">Group</label>
              <select
                id="group-select"
                name="group_category"
                value={formData.group_category}
                onChange={handleChange}
              >
                {GROUP_CATEGORIES.map((group) => (
                  <option key={group.value} value={group.value}>
                    {group.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.group_category === 'custom' && (
              <div className="form-group">
                <label htmlFor="custom-group-input">Custom Group Name</label>
                <input
                  id="custom-group-input"
                  type="text"
                  name="custom_group"
                  value={formData.custom_group}
                  onChange={handleChange}
                  placeholder="Enter custom group"
                  maxLength="50"
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="birthday-input">Birthday</label>
            <input
              id="birthday-input"
              type="date"
              name="birthday"
              value={formData.birthday}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="comment-input">Comment</label>
            <textarea
              id="comment-input"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              placeholder="Add any notes or comments"
              maxLength="500"
            />
          </div>

          <div className="form-group">
            <div className="checkbox-group">
              <input
                id="favorite-checkbox"
                type="checkbox"
                name="is_favorite"
                checked={formData.is_favorite}
                onChange={handleChange}
              />
              <label htmlFor="favorite-checkbox">Add to favorites</label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              id="modal-cancel-btn"
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              id="modal-save-btn"
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
